/**
 * US-002/US-016 商户通道资金真联调
 * GET /merchant/api/v1/positions
 * GET /merchant/api/v1/positions/{currency}/ledger
 * GET /merchant/api/v1/position-topups
 *
 * 科目为通道资金（即头寸），≠ 用户充值本金 / 佣金；金额字段均为 string。
 */
import {
  DEFAULT_MERCHANT_ID,
  loadSession,
  merchantGet,
  merchantPost,
  USE_MOCK,
  type ApiResult,
} from './client';
import {
  MERCHANT_IDENTITY_MISSING_ERROR,
  resolveMerchantIdFromSession,
  toMerchantIdString,
} from './authCore.ts';

/** 头寸预警状态（对齐 PositionWarningStatus） */
export type PositionWarningStatus = 'OK' | 'WARN' | 'CRITICAL';

/** API-POS-001 商户头寸项 */
export type PositionItem = {
  currency: string;
  balance: string;
  held: string;
  reserved: string;
  safetyBuffer: string;
  availableForReturn: string;
  /** 可划转额度（含 transfer_hold 扣减）；划转发起页真源 */
  availableForCredit: string;
  /** 划转冻结头寸 */
  transferHold: string;
  warningStatus: PositionWarningStatus;
  updatedAt: string;
};

/** API-POS-010 头寸流水条目 */
export type PositionLedgerEntry = {
  entryId: string;
  type: string;
  amount: string;
  currency: string;
  refType: string;
  refId: string;
  createdAt: string;
};

type PositionListResult = {
  total: number;
  items: PositionItem[];
};

export type PositionLedgerListResult = {
  total: number;
  items: PositionLedgerEntry[];
};

/** PAGE-MCH-FIN-001 通道资金充值地址 */
export type ChannelDepositAddress = {
  opened: boolean;
  currency: string;
  asset?: string | null;
  network?: string | null;
  address?: string | null;
  memoTag?: string | null;
  qrPayload?: string | null;
  minAmount?: string | null;
  message?: string | null;
};

/** API-POS-003 头寸充值单（通道头寸充值，≠ 用户充值本金） */
export type PositionTopup = {
  topupId: string;
  status: string;
  amount: string;
  currency: string;
  channel: string;
  network: string;
  payAddress: string;
  payMemo: string;
  createdAt: string;
};

type PositionTopupListResult = {
  total: number;
  items: PositionTopup[];
};

/** 充值单状态中文标签（对齐 PositionTopupStatus） */
export const TOPUP_STATUS_LABEL: Record<string, string> = {
  PENDING_PAYMENT: '待付款',
  DETECTING: '链上检测中',
  PENDING_REVIEW: '待人工复核',
  CREDITED: '已到账',
  EXCEPTION: '异常',
  CANCELLED: '已取消',
};

export {
  LEDGER_REF_TYPE_LABEL,
  LEDGER_TYPE_LABEL,
  ledgerRefTypeLabel,
  ledgerTypeLabel,
} from '../lib/ledgerLabels';

/** 预警状态中文 */
export const WARNING_STATUS_LABEL: Record<PositionWarningStatus, string> = {
  OK: '水位正常',
  WARN: '水位偏低',
  CRITICAL: '余额不足告警',
};

/** 头寸调回单（对齐 PositionReturnView / API-POS-007；金额均为 string） */
export type PositionReturn = {
  returnId: string;
  /** 商户内部数字 id（十进制字符串；19 位雪花不得 Number 往返） */
  merchantId: string | null;
  status: string;
  amount: string;
  currency: string;
  payoutAccountId: string;
  reason: string | null;
  createdAt: string | null;
};

type PositionReturnListResult = {
  total: number;
  items: PositionReturn[];
};

/** 头寸调回申请结果（对齐 PositionReturnApplyResult / API-POS-006） */
type PositionReturnApplyResult = {
  returnId: string;
  status: string;
  amount: string;
  currency: string;
  availableForReturn: string;
};

/** 调回状态中文标签（对齐 PositionReturnStatus 8 值；参照 TOPUP_STATUS_LABEL 先例） */
export const POSITION_RETURN_STATUS_LABEL: Record<string, string> = {
  SUBMITTED: '已提交',
  FINANCE_REVIEW: '财务复核',
  APPROVED: '已通过',
  BANK_PROCESSING: '出金处理中',
  COMPLETED: '已完成',
  REJECTED: '已驳回',
  CANCELLED: '已撤销',
  BANK_RETURNED: '银行退回',
};

/**
 * D7 调回可撤白名单：仅 SUBMITTED/FINANCE_REVIEW 可撤（对齐 PositionReturn.cancel()，
 * 否则后端回 POSITION_RETURN_CANCEL_FORBIDDEN；CANCELLED 幂等）。
 */
export const POSITION_RETURN_CANCELLABLE: ReadonlySet<string> = new Set([
  'SUBMITTED',
  'FINANCE_REVIEW',
]);

export function isPositionReturnCancellable(status: string): boolean {
  return POSITION_RETURN_CANCELLABLE.has(status);
}

/**
 * 解析当前会话商户号（AC-3 fail-close）：
 * 显式入参（十进制字符串，BigInt 瞬时校验 Long 上限）优先；否则只认 `me.merchantId`。
 * 无 me / 非法 → null（调用方写路径必须拦截，禁止回落 DEFAULT_MERCHANT_ID）。
 */
export function resolveMerchantId(merchantId?: string | null): string | null {
  const explicit = toMerchantIdString(merchantId);
  if (explicit != null) {
    return explicit;
  }
  return resolveMerchantIdFromSession(loadSession());
}

// ================= mock（头寸科目演示数据） =================

const MOCK_POSITIONS: PositionItem[] = [
  {
    currency: 'USD',
    balance: '98320.00000000',
    held: '1000.00000000',
    reserved: '2000.00000000',
    safetyBuffer: '500.00000000',
    availableForReturn: '94820.00000000',
    transferHold: '0.00000000',
    availableForCredit: '94820.00000000',
    warningStatus: 'OK',
    updatedAt: '2026-08-03T10:15:00',
  },
  {
    currency: 'EUR',
    balance: '15200.00000000',
    held: '0.00000000',
    reserved: '500.00000000',
    safetyBuffer: '500.00000000',
    availableForReturn: '14200.00000000',
    transferHold: '0.00000000',
    availableForCredit: '14200.00000000',
    warningStatus: 'WARN',
    updatedAt: '2026-08-03T09:40:00',
  },
  {
    currency: 'USDT',
    balance: '3200.00000000',
    held: '800.00000000',
    reserved: '0.00000000',
    safetyBuffer: '500.00000000',
    availableForReturn: '1900.00000000',
    transferHold: '0.00000000',
    availableForCredit: '1900.00000000',
    warningStatus: 'CRITICAL',
    updatedAt: '2026-08-02T18:20:00',
  },
];

const MOCK_LEDGER: PositionLedgerEntry[] = [
  {
    entryId: '91001',
    type: 'TOPUP',
    amount: '22000.00000000',
    currency: 'USD',
    refType: 'TOPUP',
    refId: '88001',
    createdAt: '2026-08-02T17:05:00',
  },
  {
    entryId: '91002',
    type: 'RETURN_HOLD',
    amount: '1000.00000000',
    currency: 'USD',
    refType: 'RETURN',
    refId: '77001',
    createdAt: '2026-08-02T16:30:00',
  },
  {
    entryId: '91003',
    type: 'TOPUP',
    amount: '15000.00000000',
    currency: 'USD',
    refType: 'TOPUP',
    refId: '88002',
    createdAt: '2026-08-02T16:00:00',
  },
  {
    entryId: '91004',
    type: 'RETURN_COMPLETE',
    amount: '500.00000000',
    currency: 'USD',
    refType: 'RETURN',
    refId: '77000',
    createdAt: '2026-08-02T12:00:00',
  },
  {
    entryId: '91005',
    type: 'RESERVE_CHANGE',
    amount: '2000.00000000',
    currency: 'USD',
    refType: 'RESERVE',
    refId: 'R-USD-1',
    createdAt: '2026-08-01T09:00:00',
  },
  {
    entryId: '92001',
    type: 'TOPUP',
    amount: '8000.00000000',
    currency: 'EUR',
    refType: 'TOPUP',
    refId: '88010',
    createdAt: '2026-08-02T11:20:00',
  },
  {
    entryId: '92002',
    type: 'ADJUST',
    amount: '200.00000000',
    currency: 'EUR',
    refType: 'ADJUST',
    refId: 'ADJ-1',
    createdAt: '2026-08-01T15:00:00',
  },
  {
    entryId: '93001',
    type: 'TOPUP',
    amount: '5000.00000000',
    currency: 'USDT',
    refType: 'TOPUP',
    refId: '88020',
    createdAt: '2026-08-02T08:10:00',
  },
  {
    entryId: '93002',
    type: 'RETURN_HOLD',
    amount: '800.00000000',
    currency: 'USDT',
    refType: 'RETURN',
    refId: '77010',
    createdAt: '2026-08-02T18:00:00',
  },
];

/** 商户通道头寸列表 */
export function listPositions(merchantId?: string | null) {
  const mockMerchantId = resolveMerchantId(merchantId);
  const isDefault = mockMerchantId === String(DEFAULT_MERCHANT_ID);
  return merchantGet<PositionListResult>('/positions', () => ({
    total: isDefault ? MOCK_POSITIONS.length : 0,
    items: isDefault ? MOCK_POSITIONS.map((p) => ({ ...p })) : [],
  }));
}

/** 指定币种头寸流水 */
export function listPositionLedger(currency: string, merchantId?: string | null) {
  const mockMerchantId = resolveMerchantId(merchantId);
  const ccy = (currency || 'USD').toUpperCase();
  const isDefault = mockMerchantId === String(DEFAULT_MERCHANT_ID);
  return merchantGet<PositionLedgerListResult>(
    `/positions/${encodeURIComponent(ccy)}/ledger`,
    () => {
      const items = isDefault
        ? MOCK_LEDGER.filter((e) => e.currency.toUpperCase() === ccy).map((e) => ({ ...e }))
        : [];
      return { total: items.length, items };
    },
  );
}

/** PAGE-MCH-FIN-001 通道资金充值地址 */
export function getChannelDepositAddress(currency?: string, merchantId?: string | null) {
  const mockMerchantId = resolveMerchantId(merchantId);
  const ccy = (currency || 'USD').toUpperCase();
  const qs = new URLSearchParams({ currency: ccy });
  return merchantGet<ChannelDepositAddress>(
    `/finance/channel/deposit-address?${qs.toString()}`,
    () => ({
      opened: true,
      currency: ccy,
      asset: ccy,
      network: 'BANK',
      address: `BANK-POS-${mockMerchantId}-${ccy}`,
      memoTag: null,
      qrPayload: `BANK-POS-${mockMerchantId}-${ccy}`,
      minAmount: '100',
      message: null,
    }),
  );
}

/** 通道头寸充值单列表（API-POS-003；US-016） */
export function listPositionTopups(merchantId?: string | null) {
  const mockMerchantId = resolveMerchantId(merchantId);
  const isDefault = mockMerchantId === String(DEFAULT_MERCHANT_ID);
  return merchantGet<PositionTopupListResult>('/position-topups', () => ({
    total: isDefault ? MOCK_TOPUPS.length : 0,
    items: isDefault ? MOCK_TOPUPS.map((t) => ({ ...t })) : [],
  }));
}

const MOCK_TOPUPS: PositionTopup[] = [
  {
    topupId: '88001',
    status: 'CREDITED',
    amount: '22000.00000000',
    currency: 'USD',
    channel: 'BANK',
    network: 'SWIFT',
    payAddress: '004-xxx-xxxxxx',
    payMemo: 'TOP-22000-USD',
    createdAt: '2026-08-02T17:05:00',
  },
  {
    topupId: '88002',
    status: 'CREDITED',
    amount: '15000.00000000',
    currency: 'USD',
    channel: 'BANK',
    network: 'SWIFT',
    payAddress: '004-xxx-xxxxxx',
    payMemo: 'TOP-15000-USD',
    createdAt: '2026-08-02T16:00:00',
  },
  {
    topupId: '88010',
    status: 'CREDITED',
    amount: '8000.00000000',
    currency: 'EUR',
    channel: 'BANK',
    network: 'SEPA',
    payAddress: 'IBAN-xxxx',
    payMemo: 'TOP-8000-EUR',
    createdAt: '2026-08-02T11:20:00',
  },
  {
    topupId: '88020',
    status: 'PENDING_REVIEW',
    amount: '5000.00000000',
    currency: 'USDT',
    channel: 'CRYPTO',
    network: 'TRC20',
    payAddress: 'TX9Hk3mN7pQ2rS...a8B1',
    payMemo: 'TOP-5000-USDT',
    createdAt: '2026-08-02T08:10:00',
  },
];


const MOCK_POSITION_RETURNS: PositionReturn[] = [
  {
    returnId: '77001',
    merchantId: '10',
    status: 'SUBMITTED',
    amount: '1000.00000000',
    currency: 'USD',
    payoutAccountId: '900',
    reason: '通道资金回拨',
    createdAt: '2026-08-02T16:30:00',
  },
  {
    returnId: '77000',
    merchantId: '10',
    status: 'COMPLETED',
    amount: '500.00000000',
    currency: 'USD',
    payoutAccountId: '900',
    reason: '调回测试',
    createdAt: '2026-08-02T12:00:00',
  },
  {
    returnId: '77002',
    merchantId: '10',
    status: 'FINANCE_REVIEW',
    amount: '200.00000000',
    currency: 'EUR',
    payoutAccountId: '901',
    reason: null,
    createdAt: '2026-08-03T09:10:00',
  },
];

/** API-POS-002 申请头寸充值指引（租户身份只取 Merchant JWT，body 不传 merchantId） */
export function applyPositionTopup(input: {
  amount: string;
  currency?: string;
  channel?: string;
  network?: string;
}) {
  return merchantPost<PositionTopup>(
    '/position-topups',
    {
      amount: input.amount,
      currency: input.currency || 'USD',
      channel: input.channel || 'CRYPTO',
      network: input.channel === 'CRYPTO' ? (input.network || 'TRC20') : undefined,
    },
    () => ({
      topupId: String(Date.now()),
      status: 'PENDING_PAYMENT',
      amount: input.amount,
      currency: input.currency || 'USD',
      channel: input.channel || 'CRYPTO',
      network: input.network || 'TRC20',
      payAddress: 'TDEMO-PAY-ADDRESS',
      payMemo: 'TOP-DEMO',
      createdAt: new Date().toISOString(),
    }),
  );
}

/**
 * API-POS-006 头寸调回申请（Evaluator 修订 1：body 必含会话 merchantId，
 * PositionReturnApplyRequest.merchantId @NotNull，缺之必 400 VALIDATION_ERROR；
 * AC-3：无 me.merchantId 时 fail-close——不发请求，返回明确错误；禁硬编码 20/跨租户；
 * Idempotency-Key 由 client 自动注入）。
 */
export function applyPositionReturn(input: {
  amount: string;
  currency?: string;
  payoutAccountId: string;
  reason?: string;
}): Promise<ApiResult<PositionReturnApplyResult>> {
  const merchantId = resolveMerchantId();
  if (merchantId == null) {
    return Promise.resolve({ ok: false, error: MERCHANT_IDENTITY_MISSING_ERROR });
  }
  return merchantPost<PositionReturnApplyResult>(
    '/position-returns',
    {
      merchantId,
      amount: input.amount,
      currency: input.currency || 'USD',
      payoutAccountId: input.payoutAccountId,
      reason: input.reason?.trim() ? input.reason.trim() : undefined,
    },
    () => ({
      returnId: String(Date.now()),
      status: 'SUBMITTED',
      amount: input.amount,
      currency: input.currency || 'USD',
      availableForReturn: '0.00000000',
    }),
  );
}

/** API-POS-007 调回列表（后端无分页参数，全量展示） */
export function listPositionReturns() {
  return merchantGet<PositionReturnListResult>('/position-returns', () => ({
    total: MOCK_POSITION_RETURNS.length,
    items: MOCK_POSITION_RETURNS.map((r) => ({ ...r })),
  }));
}

/** API-POS-007 调回详情 */
export function getPositionReturn(returnId: string) {
  const id = String(returnId);
  return merchantGet<PositionReturn>(
    `/position-returns/${encodeURIComponent(id)}`,
    () => ({ ...(MOCK_POSITION_RETURNS.find((r) => r.returnId === id) ?? MOCK_POSITION_RETURNS[0]) }),
  );
}

/** API-POS-007 调回撤销（仅财务审前；非法态后端回 POSITION_RETURN_CANCEL_FORBIDDEN 透传） */
export function cancelPositionReturn(returnId: string) {
  const id = String(returnId);
  return merchantPost<PositionReturn>(
    `/position-returns/${encodeURIComponent(id)}/cancel`,
    {},
    () => {
      const it = MOCK_POSITION_RETURNS.find((r) => r.returnId === id);
      if (it) it.status = 'CANCELLED';
      return { ...(it ?? MOCK_POSITION_RETURNS[0]) };
    },
  );
}

export { USE_MOCK };
